import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { motion } from "framer-motion"
import React, { useState, useEffect } from "react"
import { VariantReader } from "aldoo-ra/VariantReader"
import Typography from "aldoo-ra/Typography"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"
import parse from "html-react-parser"

const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick,
  variants = {},
}) => {
  const {
    item,
    question: questionClass,
    question_text,
    question_text_selected,
    toggle,
    motion_container,
    answer: answerClass,
  } = variants

  return (
    <div
      className={twMerge(
        ` border-2 border-transparent hover:border-primary hover:border-2 ${
          isOpen ? "border-primary border-2" : ""
        }`,
        item
      )}
    >
      <button
        onClick={onClick}
        className={twMerge(
          `group w-full flex justify-between items-center`,
          questionClass
        )}
      >
        <span
          className={twMerge(
            `group-hover:text-primary flex-grow pr-4`,
            isOpen ? question_text_selected : question_text
          )}
        >
          {question}
        </span>
        <div className="flex-shrink-0 flex items-center">
          <ChevronDownIcon
            className={twMerge(
              `w-6 h-6 transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              } group-hover:text-primary`,
              toggle
            )}
          />
        </div>
      </button>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={motion_container}
      >
        {React.isValidElement(answer) ? (
          <div className={answerClass}>{answer}</div>
        ) : typeof answer === 'string' ? (
          <div className={answerClass}>{parse(answer)}</div>
        ) : (
          <p className={answerClass}>{answer}</p>
        )}
      </motion.div>
    </div>
  )
}

const Accordion = ({ 
  items, 
  title, 
  variant = "aqua", 
  styleOverrides,
  localUserPreferencesKey = null, // Key for storing user preferences
}) => {
  const [openIndex, setOpenIndex] = useState(() => {
    // Try to load from user preferences first, then fall back to null
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedIndex = LocalUserPreferences.get(localUserPreferencesKey)
        if (savedIndex !== undefined && typeof savedIndex === 'number') {
          return savedIndex
        }
      } catch (error) {
        console.warn("Failed to load accordion preference:", error)
      }
    }
    return null
  })

  // Get variant styles and merge with overrides
  const variants = VariantReader.useVariant({
    componentName: "Accordion",
    variant,
    styleOverrides,
  })

  const { container, title: titleClass } = variants

  const handleAccordionClick = (index) => {
    const newIndex = openIndex === index ? null : index
    setOpenIndex(newIndex)
    
    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(localUserPreferencesKey, newIndex)
      } catch (error) {
        console.warn("Failed to save accordion preference:", error)
      }
    }
  }

  return (
    <div className={container}>
      {title && <Typography className={titleClass}>{title}</Typography>}
      {(items && Array.isArray(items) ? items : []).map((item, index) => (
        <AccordionItem
          key={index}
          question={item.question || item.title}
          answer={item.answer}
          isOpen={openIndex === index}
          onClick={() => handleAccordionClick(index)}
          variants={variants}
        />
      ))}
    </div>
  )
}

export default Accordion
